import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, AlertCircle, Camera } from "lucide-react";
import SectionTitle from "./SectionTitle";
import GalleryCard from "./GalleryCard";
import { countGalleryImages } from "../api/services";
import { useGalleryPage } from "../api/queries";

const PREVIEW_ALBUM_COUNT = 4;

const DEFAULT_SECTION = {
  tagline: "Life at SSA Techno",
  title: "Moments That Define Our Culture",
  subtitle:
    "Anniversaries, celebrations, team outings, and milestones — a glimpse of the people and spirit behind every project we deliver.",
};

function AlbumSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: PREVIEW_ALBUM_COUNT }).map((_, n) => (
        <div key={n} className="overflow-hidden rounded-xl border border-white/10 bg-white/5 animate-pulse">
          <div className="aspect-[4/3] bg-white/10" />
          <div className="space-y-3 p-5">
            <div className="h-3 w-1/3 rounded bg-white/10" />
            <div className="h-5 w-4/5 rounded bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GalleryPreview() {
  const { data, isLoading: loading, error } = useGalleryPage();

  const pageMeta = data?.header ?? null;
  const items = data?.items ?? [];
  const albums = useMemo(() => items.slice(0, PREVIEW_ALBUM_COUNT), [items]);
  const totalImages = useMemo(() => countGalleryImages(items), [items]);
  const errorMessage = error?.message || null;

  if (!loading && !errorMessage && albums.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-[#0a1a2f] py-24 lg:py-28">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(30,111,207,0.18),_transparent_55%)]" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-[#1e6fcf]/10 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mb-12 flex flex-col gap-8 lg:mb-14 lg:flex-row lg:items-end lg:justify-between">
          <SectionTitle
            light
            eyebrow={pageMeta?.tagline || DEFAULT_SECTION.tagline}
            title={pageMeta?.title || DEFAULT_SECTION.title}
            subtitle={pageMeta?.subtitle || DEFAULT_SECTION.subtitle}
          />

          <div className="flex shrink-0 flex-col items-start gap-4 sm:flex-row sm:items-center lg:flex-col lg:items-end">
            {!loading && totalImages > 0 && (
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-[#aad4fa]">
                <Camera className="h-4 w-4" />
                {totalImages}+ memorable moments
              </div>
            )}
            <Link
              to="/gallery"
              className="hidden items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-5 py-3 text-sm font-bold text-white transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] lg:inline-flex"
            >
              View Full Gallery
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {loading ? (
          <AlbumSkeleton />
        ) : errorMessage ? (
          <div className="rounded-2xl border border-red-400/30 bg-red-500/10 py-12 text-center">
            <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-300" />
            <p className="text-sm font-semibold text-red-200">{errorMessage}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {albums.map((album) => (
              <GalleryCard key={album.id} album={album} compact />
            ))}
          </div>
        )}

        <div className="mt-10 text-center lg:hidden">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 rounded-lg bg-[#1e6fcf] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1758a8]"
          >
            View Full Gallery
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
